import type { Feature, FeatureCollection, Geometry } from 'geojson';

function extendBboxFromRing(
  ring: number[][],
  acc: { minLng: number; maxLng: number; minLat: number; maxLat: number }
): void {
  for (const pt of ring) {
    if (!pt || pt.length < 2) continue;
    const lng = pt[0];
    const lat = pt[1];
    if (typeof lng !== 'number' || typeof lat !== 'number' || Number.isNaN(lng) || Number.isNaN(lat)) {
      continue;
    }
    acc.minLng = Math.min(acc.minLng, lng);
    acc.maxLng = Math.max(acc.maxLng, lng);
    acc.minLat = Math.min(acc.minLat, lat);
    acc.maxLat = Math.max(acc.maxLat, lat);
  }
}

function extendBboxFromGeometry(
  geometry: Geometry,
  acc: { minLng: number; maxLng: number; minLat: number; maxLat: number }
): void {
  if (geometry.type === 'Polygon') {
    for (const ring of geometry.coordinates) {
      extendBboxFromRing(ring, acc);
    }
  } else if (geometry.type === 'MultiPolygon') {
    for (const poly of geometry.coordinates) {
      for (const ring of poly) {
        extendBboxFromRing(ring, acc);
      }
    }
  }
}

/**
 * Axis-aligned bounding box for a FeatureCollection (Polygon / MultiPolygon only).
 * Returns null if no usable geometry.
 */
export function geoFeatureCollectionBBox(
  fc: FeatureCollection
): { sw: [number, number]; ne: [number, number] } | null {
  const acc = {
    minLng: 180,
    maxLng: -180,
    minLat: 90,
    maxLat: -90,
  };

  for (const f of fc.features as Feature[]) {
    if (!f.geometry) continue;
    extendBboxFromGeometry(f.geometry, acc);
  }

  if (acc.minLng > acc.maxLng || acc.minLat > acc.maxLat) return null;
  // Skip world-wrapping boxes (e.g. Russia + USA) — default camera is safer.
  if (acc.maxLng - acc.minLng > 160) return null;

  return {
    sw: [acc.minLng, acc.minLat],
    ne: [acc.maxLng, acc.maxLat],
  };
}
